import { INVENTORIZATION_BLOCK_IDS } from '@/features/inventorization/domain/inventorization';
import {
  type AccessibilityState,
  readAccessibility,
} from '@/features/inventorization/domain/inventorization-technical-accessibility';
import {
  type MeterMountTypeSelection,
  readMountType,
} from '@/features/inventorization/domain/inventorization-technical-mount-type';
import { normalizeTechnicalDetails } from '@/features/inventorization/domain/inventorization-technical-normalization';
import type {
  InventorizationOutput,
  OutputTechnologySelection,
} from '@/features/inventorization/domain/inventorization-technical-outputs';
import { readOutputs, readOutputTechnology } from '@/features/inventorization/domain/inventorization-technical-outputs';
import {
  getInventorizationTechnicalBlockStatus,
  validateInventorizationTechnicalDetails,
} from '@/features/inventorization/domain/inventorization-technical-validation';
import type { CaptureSession } from '@/shared/capture/capture-session';

export {
  type AccessibilityState,
  isMeterAccessibilitySelection,
  readAccessibilitySelectionValue,
} from '@/features/inventorization/domain/inventorization-technical-accessibility';
export {
  isRemoteReadingMountType,
  type MeterMountTypeSelection,
  readInventoryMeterMountType,
  readMountType,
} from '@/features/inventorization/domain/inventorization-technical-mount-type';
export { normalizeTechnicalDetails } from '@/features/inventorization/domain/inventorization-technical-normalization';
export {
  type InventorizationOutput,
  mergeInbuiltOutputs,
  readInbuiltOutputTechnologies,
  readOutputTechnology,
} from '@/features/inventorization/domain/inventorization-technical-outputs';
export {
  getInventorizationTechnicalBlockStatus,
  validateInventorizationTechnicalDetails,
} from '@/features/inventorization/domain/inventorization-technical-validation';

export interface InventorizationTechnicalDetails {
  accessibility: AccessibilityState;
  detail: Record<string, string>;
  monitoredEntity: string;
  mountTechnology: OutputTechnologySelection;
  mountType: MeterMountTypeSelection;
  note: string;
  outputs: InventorizationOutput[];
  remoteReadingOwner: string;
  remoteReadingSerial: string;
}

export function readInventorizationTechnicalDetails(session: CaptureSession): InventorizationTechnicalDetails {
  const inventorization = session.data.inventorization as
    | { technical?: Partial<InventorizationTechnicalDetails> }
    | undefined;
  const technical = inventorization?.technical ?? {};

  return normalizeTechnicalDetails({
    accessibility: readAccessibility(technical.accessibility),
    detail: readStringRecord(technical.detail),
    monitoredEntity: technical.monitoredEntity ?? '',
    mountTechnology: readOutputTechnology(technical.mountTechnology),
    mountType: readMountType(technical.mountType),
    note: technical.note ?? '',
    outputs: readOutputs(technical.outputs),
    remoteReadingOwner: technical.remoteReadingOwner ?? '',
    remoteReadingSerial: technical.remoteReadingSerial ?? '',
  });
}

export function updateInventorizationTechnicalDetails(
  session: CaptureSession,
  patch: Partial<InventorizationTechnicalDetails>,
  updatedAt: string,
): CaptureSession {
  const technical = normalizeTechnicalDetails({
    ...readInventorizationTechnicalDetails(session),
    ...patch,
  });
  const issues = validateInventorizationTechnicalDetails(technical);

  return {
    ...session,
    blockStatuses: {
      ...session.blockStatuses,
      [INVENTORIZATION_BLOCK_IDS.technical]: getInventorizationTechnicalBlockStatus(technical),
    },
    data: {
      ...session.data,
      inventorization: {
        ...(session.data.inventorization as object),
        technical,
      },
    },
    updatedAt,
    validationErrors: [
      ...session.validationErrors.filter(issue => issue.blockId !== INVENTORIZATION_BLOCK_IDS.technical),
      ...issues,
    ],
  };
}

function readStringRecord(value: unknown) {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value)
      .filter((entry): entry is [string, string] => typeof entry[1] === 'string')
      .toSorted(([left], [right]) => left.localeCompare(right)),
  );
}
